import { useEffect, useRef, useState } from "react";
import {
  CorrectionContextSchema,
  PlaceSuggestionsSchema,
  VisitCorrectionInputSchema,
  type ApiResponse,
  type CorrectionContext,
  type PlaceSuggestions as Suggestions,
  type VisitCorrectionInput,
} from "@smrti/types";

type Role = "home" | "work";
type Pending = { role: Role; visitId: string; input: VisitCorrectionInput };
type Editor = { role: Role; context: CorrectionContext; label: string; candidateId: string };
const uncertainMessage =
  "The confirmation outcome is unknown. Retry the same change before making another choice.";
function restorePending(key: string): Pending | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) ?? "null");
    if (!saved || !["home", "work"].includes(saved.role) || typeof saved.visitId !== "string")
      return null;
    return {
      role: saved.role,
      visitId: saved.visitId,
      input: VisitCorrectionInputSchema.parse(saved.input),
    };
  } catch {
    return null;
  }
}
class ResponseError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}
async function request<T>(url: string, input?: unknown): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...(input === undefined
      ? {}
      : {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }),
  });
  const body = (await response.json()) as ApiResponse<T>;
  if (!response.ok || !body.ok)
    throw new ResponseError(body.ok ? "Request failed" : body.error, response.status);
  return body.data;
}

export function PlaceSuggestions({
  apiBase,
  userId,
  onConfirmed,
}: {
  apiBase: string;
  userId: string;
  onConfirmed: () => void;
}) {
  const storageKey = `smrti:place-confirmation:${userId}`;
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(() => restorePending(storageKey));
  const [error, setError] = useState<string | null>(pending ? uncertainMessage : null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [reload, setReload] = useState(0);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    request<unknown>(`${apiBase}/api/place/suggestions?timezone=${encodeURIComponent(timezone)}`)
      .then((data) => {
        if (!cancelled) setSuggestions(PlaceSuggestionsSchema.parse(data));
      })
      .catch(() => {
        if (!cancelled) setError("Place suggestions are unavailable. Try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, reload]);
  const hide = (role: Role) => setSuggestions((current) => current && { ...current, [role]: null });
  const refresh = () => {
    setSuggestions(null);
    setReload((n) => n + 1);
  };

  async function send(operation: Pending, retry = false) {
    setBusy(true);
    setError(null);
    setPending(operation);
    try {
      // Persist before sending so navigation/reload cannot invent a new retry.
      sessionStorage.setItem(storageKey, JSON.stringify(operation));
      await request(
        `${apiBase}/api/location/visits/${encodeURIComponent(operation.visitId)}/correct`,
        operation.input
      );
      // An obsolete caller must not erase a newer caller's pending operation.
      // Keep this operation for exact replay when navigation interrupted it.
      if (!active.current) return;
      hide(operation.role);
      sessionStorage.removeItem(storageKey);
      setPending(null);
      setEditor(null);
      onConfirmed();
    } catch (e) {
      if (!active.current) return;
      if (
        e instanceof ResponseError &&
        ([400, 404, 409].includes(e.status) || (e.status === 401 && !retry))
      ) {
        sessionStorage.removeItem(storageKey);
        setPending(null);
        setEditor(null);
        if (e.status === 401) setSuggestions(null);
        else refresh();
        setError(
          e.status === 409
            ? "This place changed. Review the refreshed suggestion before confirming."
            : e.status === 401
              ? "Your session expired. Sign in again before confirming this place."
              : e.message
        );
      } else {
        // A rejected retry says nothing about the original request's outcome.
        setError(
          e instanceof ResponseError && e.status === 401
            ? "Your session expired. Sign in again to retry the same confirmation."
            : uncertainMessage
        );
      }
    } finally {
      if (active.current) setBusy(false);
    }
  }

  async function open(role: Role, confirm: boolean) {
    const suggestion = suggestions?.[role];
    if (!suggestion) return;
    setBusy(true);
    setError(null);
    try {
      const context = CorrectionContextSchema.parse(
        await request<unknown>(
          `${apiBase}/api/location/visits/${encodeURIComponent(suggestion.visitId)}/correction-context`
        )
      );
      if (!active.current) return;
      if (context.placeId !== suggestion.placeId) {
        refresh();
        setError("This visit moved to another place. Review the refreshed suggestion.");
        return;
      }
      if (confirm) {
        await send({
          role,
          visitId: suggestion.visitId,
          input: {
            scope: "place",
            operationId: crypto.randomUUID(),
            contextToken: context.contextToken,
            selection: { kind: "custom", label: role === "home" ? "Home" : "Work" },
          },
        });
      } else setEditor({ role, context, label: context.placeName ?? "", candidateId: "" });
    } catch {
      if (active.current) setError("Could not load this place.");
    } finally {
      if (active.current) setBusy(false);
    }
  }

  async function snooze(role: Role) {
    setBusy(true);
    setError(null);
    try {
      await request(`${apiBase}/api/place/suggestions/snooze`, { role });
      hide(role);
    } catch {
      setError("Could not snooze this suggestion. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!error && !suggestions?.home && !suggestions?.work) return null;
  return (
    <section aria-label="Home and Work suggestions" className="mb-6 space-y-3">
      <h2 className="text-sm font-semibold text-stone-800">Your regular places</h2>
      <p className="text-xs text-stone-500">
        Suggestions from recent visit patterns, not confirmed locations.
      </p>
      {error && (
        <div role="alert" className="text-sm text-red-700">
          <p>{error}</p>
          <button
            type="button"
            disabled={busy}
            className="underline mt-1"
            onClick={() => (pending ? void send(pending, true) : (setError(null), refresh()))}
          >
            {pending ? "Retry same confirmation" : "Reload suggestions"}
          </button>
        </div>
      )}
      {(["home", "work"] as const).map((role) => {
        const suggestion = suggestions?.[role];
        if (!suggestion) return null;
        const title = role === "home" ? "Home" : "Work";
        return (
          <article key={role} className="bg-white border border-stone-200 rounded p-4 space-y-2">
            <h3 className="font-medium text-stone-900 text-sm">
              Is this {role === "home" ? "home" : "work"}?
            </h3>
            <p className="text-sm text-stone-700">
              {suggestion.label ??
                `Saved place near ${suggestion.latitude.toFixed(4)}, ${suggestion.longitude.toFixed(4)}`}
            </p>
            {suggestion.formattedAddress && (
              <p className="text-xs text-stone-500">{suggestion.formattedAddress}</p>
            )}
            <p className="text-xs text-stone-500">
              {suggestion.qualifyingDays} qualifying days · {Math.round(suggestion.hours)}{" "}
              {role === "home" ? "overnight" : "weekday daytime"} hours
            </p>
            <p className="text-xs text-stone-500">
              Confirming names this saved place for all its linked visits. It does not confirm each
              visit’s membership.
            </p>
            <div className="flex flex-wrap gap-3 text-sm">
              <button
                type="button"
                disabled={busy || !!pending || !!editor}
                onClick={() => void open(role, true)}
                className="rounded px-3 py-2 bg-stone-900 text-white disabled:opacity-50"
              >
                Yes, set as {title}
              </button>
              <button
                type="button"
                disabled={busy || !!pending || !!editor}
                onClick={() => void open(role, false)}
                className="underline disabled:opacity-50"
              >
                Not quite
              </button>
              <button
                type="button"
                disabled={busy || !!pending || !!editor}
                onClick={() => void snooze(role)}
                className="underline disabled:opacity-50"
              >
                Snooze 14 days
              </button>
            </div>
            {editor?.role === role && (
              <form
                className="space-y-3 border-t border-stone-200 pt-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  void send({
                    role,
                    visitId: editor.context.visitId,
                    input: {
                      scope: "place",
                      operationId: crypto.randomUUID(),
                      contextToken: editor.context.contextToken,
                      selection: editor.candidateId
                        ? { kind: "candidate", candidateId: editor.candidateId }
                        : { kind: "custom", label: editor.label.trim() },
                    },
                  });
                }}
              >
                <p className="text-xs text-stone-600">
                  Correct the saved place name for {editor.context.linkedVisitCount} linked visits.
                </p>
                <label className="block text-sm">
                  Saved candidate
                  <select
                    disabled={busy || !!pending}
                    value={editor.candidateId}
                    onChange={(e) => setEditor({ ...editor, candidateId: e.target.value })}
                    className="block w-full border border-stone-300 rounded p-2 mt-1"
                  >
                    <option value="">Use a custom name</option>
                    {editor.context.candidates.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.formattedAddress ?? c.id}
                      </option>
                    ))}
                  </select>
                </label>
                {!editor.candidateId && (
                  <label className="block text-sm">
                    Place name
                    <input
                      autoFocus
                      maxLength={200}
                      disabled={busy || !!pending}
                      value={editor.label}
                      onChange={(e) => setEditor({ ...editor, label: e.target.value })}
                      className="block w-full border border-stone-300 rounded p-2 mt-1"
                    />
                  </label>
                )}
                <div className="flex gap-3 text-sm">
                  <button
                    disabled={busy || !!pending || (!editor.candidateId && !editor.label.trim())}
                    className="rounded px-3 py-2 bg-stone-900 text-white disabled:opacity-50"
                  >
                    Save place name
                  </button>
                  <button
                    type="button"
                    disabled={busy || !!pending}
                    onClick={() => setEditor(null)}
                    className="underline disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </article>
        );
      })}
    </section>
  );
}
